import clsx from "clsx";
import { useState, useEffect, useRef } from "react";
import { ChevronDownIcon } from "../../assets/icons";

interface SelectOption {
	value: string;
	label: string;
}

interface SelectProps {
	options: SelectOption[];
	value?: string;
	onChange: (value: string) => void;
	placeholder?: string;
	iconColor?: string;
	customClassName?: string;
	customClassNameUl?: string;
}

const Select = ({
	options,
	value,
	onChange,
	placeholder = "Select an option",
	iconColor,
	customClassName,
	customClassNameUl,
}: SelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectRef = useRef<HTMLDivElement>(null);
	const ulRef = useRef<HTMLUListElement>(null);

	const handleSelect = (selectedValue: string) => {
		onChange(selectedValue);
		setIsOpen(false);
	};

	useEffect(() => {
		if (isOpen && ulRef.current) {
			ulRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [isOpen]);
	const handleClickOutside = (event: MouseEvent) => {
		if (
			selectRef.current &&
			!selectRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div className="relative inline-block w-full" ref={selectRef}>
			<div
				className={clsx(
					"h-16 w-full text-z-shark font-medium text-sm px-3 rounded-xl border border-[#EAEAEA] placeholder:text-[#D1D1D1]",
					customClassName,
				)}
				onClick={() => setIsOpen(!isOpen)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						setIsOpen(!isOpen);
					}
				}}
			>
				<div className="flex items-center h-full">
					{value ? (
						options.find((option) => option.value === value)?.label
					) : (
						<div className="text-[#909090] font-normal">{placeholder}</div>
					)}
				</div>
				<div className="absolute inset-y-0 right-3 flex items-center px-2 text-gray-700">
					<ChevronDownIcon fill={iconColor} />
				</div>
			</div>
			{isOpen && (
				<ul
					ref={ulRef}
					className={clsx(
						"absolute z-10 max-h-60 overflow-auto  shadow-md bg-white w-full overflow-y-auto border border-[#EAEAEA] rounded-xl top-[74px]",
						customClassNameUl,
					)}
				>
					{options.map((option) => (
						<li
							key={option.value}
							className="h-12 text-black flex items-center border-t border-t-[#EAEAEA] first:border-t-0"
							onClick={() => handleSelect(option.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleSelect(option.value);
								}
							}}
						>
							<span className="px-3">{option.label}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export { Select };
